import React from "react";
import { Link } from "react-router-dom";
import { Flame, Lightbulb, Sparkles, Zap, ArrowRight } from "lucide-react";
import { useLanguage } from "../../i18n";

const SpecialEffectsSection = () => {
  const { language } = useLanguage();
  
  const effects = [
    {
      icon: Flame,
      title: language === 'es' ? "Pirotecnia Fría" : "Cold Pyrotechnics",
      description: language === 'es' 
        ? "Efectos de fuego seguros y espectaculares sin riesgo de incendio"
        : "Safe and spectacular fire effects without fire risk"
    },
    {
      icon: Lightbulb,
      title: language === 'es' ? "Iluminación" : "Lighting",
      description: language === 'es'
        ? "Sistemas de iluminación arquitectónica y escénica de alto impacto"
        : "High-impact architectural and stage lighting systems"
    },
    {
      icon: Zap,
      title: language === 'es' ? "Láser" : "Laser",
      description: language === 'es'
        ? "Shows de láser sincronizados con música y coreografías"
        : "Laser shows synchronized with music and choreography"
    },
    {
      icon: Sparkles,
      title: language === 'es' ? "Confeti y FX" : "Confetti & FX",
      description: language === 'es'
        ? "Efectos especiales para crear momentos memorables"
        : "Special effects to create memorable moments"
    },
  ];

  return (
    <section id="special-fx" className="py-20 md:py-28 bg-[#030810] relative overflow-hidden" data-testid="special-effects-section">
      {/* Background glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {language === 'es' ? 'Efectos' : 'Special'}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
              {language === 'es' ? 'Especiales' : 'Effects'}
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {language === 'es' 
              ? 'Tecnología visual y efectos que transforman cualquier evento en una experiencia multisensorial inolvidable'
              : 'Visual technology and effects that transform any event into an unforgettable multisensory experience'}
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {effects.map((effect, index) => (
            <div 
              key={index}
              className="group p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-purple-500/30 transition-all hover:bg-white/[0.07] text-center"
              data-testid={`effect-${index}`}
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform group-hover:shadow-[0_0_30px_rgba(168,85,247,0.4)]">
                <effect.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-lg font-bold text-white mb-2">{effect.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{effect.description}</p>
            </div>
          ))}
        </div>
        
        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <a 
            href="#contacto"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-400 hover:to-pink-500 text-white font-semibold transition-all hover:shadow-[0_0_30px_rgba(168,85,247,0.4)]"
          >
            {language === 'es' ? 'Cotizar Efectos Especiales' : 'Quote Special Effects'}
          </a>
          <Link 
            to="/efectos-especiales"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full border-2 border-purple-500/50 text-purple-400 hover:bg-purple-500/10 font-semibold transition-all"
          >
            {language === 'es' ? 'Ver más' : 'Learn more'}
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SpecialEffectsSection;
